import React from 'react';
import { Home, ChevronRight, Volume2, Calendar, FileText, Landmark, Clock, ExternalLink } from 'lucide-react';
import { ArchivalImageCard } from '../ArchivalImageCard';
import { useVoiceReader } from '../../context/VoiceReaderContext';

interface DentonLifePre1850SectionProps {
  onSelectPart?: (partId: string) => void;
}

export const DentonLifePre1850Section: React.FC<DentonLifePre1850SectionProps> = ({ onSelectPart }) => {
  const { speakCurrentPage, isPlaying } = useVoiceReader();

  return (
    <div className="space-y-10">
      
      {/* Sub-navigation Breadcrumbs & Voice Narrator Controls */}
      <header className="bg-[#f3ede0] p-6 sm:p-8 md:p-10 rounded-3xl border-2 border-[#d8ceb9] shadow-sm space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3 text-xs font-serif text-[#7a5b3a]">
          
          {/* Breadcrumb: The Dentons > Part 1 of 9: Denton life in England pre-1850 */}
          <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-1.5 sm:gap-2">
            <button
              onClick={() => onSelectPart && onSelectPart('hub')}
              className="hover:underline flex items-center gap-1 font-bold text-[#7a5b3a]"
            >
              <Home className="w-3.5 h-3.5" />
              <span>The Dentons</span>
            </button>
            <ChevronRight className="w-3.5 h-3.5 text-[#d8ceb9]" />
            <span className="bg-[#e8dec9] px-2.5 py-1 rounded-full font-bold uppercase tracking-wider text-[#7a5b3a] text-[11px] sm:text-xs">
              Part 1 of 9: Denton life in England pre-1850
            </span>
          </nav>

          {/* Audio Narration Trigger */}
          <button
            onClick={speakCurrentPage}
            className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-[#7a5b3a] hover:bg-[#5c4328] text-white text-xs font-bold transition shadow-xs"
            title="Listen to Denton life in England before 1850"
          >
            <Volume2 className={`w-3.5 h-3.5 ${isPlaying ? 'animate-pulse text-amber-200' : 'text-amber-300'}`} />
            <span>{isPlaying ? 'Listening to Section...' : 'Listen to this Chapter'}</span>
          </button>
        </div>

        <h1 className="font-serif text-3xl md:text-5xl font-extrabold text-[#2c221e] tracking-tight leading-tight pt-1">
          DENTON LIFE IN ENGLAND BEFORE 1850
        </h1>

        <div className="flex items-center gap-2 text-xs font-serif text-[#7a5b3a] uppercase tracking-wider font-semibold">
          <Clock className="w-3.5 h-3.5" />
          <span>Part 1 of the 9-Part Denton Family History</span>
        </div>
      </header>

      {/* Main Archival Text Content */}
      <div className="bg-[#fbf9f4] p-6 sm:p-10 md:p-12 rounded-3xl border border-[#d8ceb9] shadow-sm space-y-8 text-base md:text-lg leading-relaxed text-[#2c221e]">
        
        {/* Intro Paragraphs */}
        <div className="space-y-4">
          <p>
            These family trees are from Foss Leach (Arthur Denton line).<sup>1</sup><br />
            This is the Denton family that came to NZ on Sir George Seymour in 1851. People with the surname Pryke had been in and around Great Barton since 1581, when a Minister called Robert Pryke was recorded as being in Denham, Suffolk. The Dentons had farmed in the Bury St Edmunds area for at least 150 years.<sup>2</sup>
          </p>

          <p>
            In the 1841 Census John Frederick Denton and his family were living at Green Farm, on the Rushbrooke estate. By late 1845 they were at Fishley Hall.
          </p>
        </div>

        {/* Image 1: Family Tree 1 */}
        <ArchivalImageCard
          src="/images/familytree1.jpg"
          alt="Denton Family Tree 1"
          caption="Denton Family Tree 1 — Line of John Frederick Denton and Anne Maria Pryke"
          filenameHint="familytree1.jpg"
        />

        <p>
          Elizabeth (aged 3) was buried at Rushbrooke; William, Georgiana and Edward died and were buried in Christchurch in unknown graves.
        </p>

        <p className="font-serif font-semibold text-[#4a3525] pt-2">
          The previous generation below.
        </p>

        {/* Image 2: Family Tree 2 */}
        <ArchivalImageCard
          src="/images/familytree2.jpg"
          alt="Denton Family Tree 2"
          caption="Denton Family Tree 2 — Ancestral generations in Suffolk"
          filenameHint="familytree2.jpg"
        />

        {/* Image 3: Family Tree 3 */}
        <ArchivalImageCard
          src="/images/familytree3.jpg"
          alt="Denton Family Tree 3"
          caption="Denton Family Tree 3 — Additional family lineages"
          filenameHint="familytree3.jpg"
        />

        {/* Image 4: Rushbrooke Hall */}
        <div className="space-y-2">
          <ArchivalImageCard
            src="/images/rushbrookehall.jpg"
            alt="Rushbrooke Hall"
            caption="Rushbrooke Hall — Queen Elizabeth 1 held court in the drawing room here in 1578."
            filenameHint="rushbrookehall.jpg"
          />
          <p className="italic text-[#5c4328] font-serif text-center sm:text-left text-sm md:text-base">
            Queen Elizabeth 1 held court in the drawing room here in 1578.
          </p>
        </div>

        <p>
          Rushbrooke<sup>3</sup> Hall was demolished in 1961 after being burnt down. It dated back to 1550 and had originally belonged to the Rushbrooke family. It was the seat of the Jermyn family for 600 years (16 generations) before Colonel Robert Rushbrooke’s father bought it in May 1808, and Robert and his bride Frances Davers<sup>4</sup>, took up residence. It was sold in 1923 to Lady Islington, who sold it 15 years later to Victory, 3rd Lord Rothschild. It was used as an army hospital during World War Two.
        </p>

        {/* Image 5: Rushbrooke Hall 1818 */}
        <div className="space-y-2">
          <ArchivalImageCard
            src="/images/rushbrookehall1.jpg"
            alt="Rushbrooke Hall 1818"
            caption="Rushbrooke Hall 1818 (note it is sometimes spelt Rushbrook) - one of the great mansions of Suffolk"
            filenameHint="rushbrookehall1.jpg"
          />
          <p className="italic text-[#5c4328] font-serif text-center sm:text-left text-sm md:text-base">
            Rushbrooke Hall 1818 (note it is sometimes spelt Rushbrook) - one of the great mansions of Suffolk
          </p>
        </div>

        {/* Timeline Section Header */}
        <div className="pt-6 pb-2 border-b-2 border-[#d8ceb9]">
          <h2 className="font-serif text-2xl md:text-3xl font-extrabold text-[#2c221e] tracking-tight">
            IN THE YEARS BEFORE THE DENTONS SET SAIL FOR NEW ZEALAND….
          </h2>
        </div>

        {/* Timeline Years (1845–1849) */}
        <div className="space-y-8 pt-2">
          
          {/* Year 1845 */}
          <div className="bg-[#f3ede0]/70 p-5 sm:p-6 rounded-2xl border-l-4 border-[#7a5b3a] space-y-3">
            <div className="flex items-center gap-2">
              <span className="bg-[#7a5b3a] text-amber-100 font-serif font-bold text-base sm:text-lg px-3 py-1 rounded-lg">
                1845
              </span>
            </div>
            <ul className="list-disc list-outside pl-6 space-y-2 text-[#2c221e]">
              <li>Colonel Rushbrooke died, after 10 years as MP for West Suffolk – he and John Denton were good friends.</li>
              <li>Development of the railway line from Norwich and Yarmuth to Cambridge began and with it a period of considerable social change.</li>
              <li>There were more than 160 arson attacks in the Bury St Edmunds area, blamed on the general unrest arising from the very difficult conditions for so many people.</li>
              <li>There was a dreadful potato famine in Ireland caused by a fungus that wiped out half the crop – about 1 million people died and the same number left Ireland.</li>
            </ul>
          </div>

          {/* Year 1846 */}
          <div className="bg-[#f3ede0]/70 p-5 sm:p-6 rounded-2xl border-l-4 border-[#7a5b3a] space-y-3">
            <div className="flex items-center gap-2">
              <span className="bg-[#7a5b3a] text-amber-100 font-serif font-bold text-base sm:text-lg px-3 py-1 rounded-lg">
                1846
              </span>
            </div>
            <ul className="list-disc list-outside pl-6 space-y-2 text-[#2c221e]">
              <li>In December the railway line arrived at Bury St Edmunds making the travel time to London only four hours – the opening was celebrated with street celebrations, fireworks and a civic banquet. It had implications for the stagecoach and transport boats unable to compete against the railway. The mail was one of the first to switch to railway deliveries.</li>
            </ul>
          </div>

          {/* Year 1847 */}
          <div className="bg-[#f3ede0]/70 p-5 sm:p-6 rounded-2xl border-l-4 border-[#7a5b3a] space-y-3">
            <div className="flex items-center gap-2">
              <span className="bg-[#7a5b3a] text-amber-100 font-serif font-bold text-base sm:text-lg px-3 py-1 rounded-lg">
                1847
              </span>
            </div>
            <ul className="list-disc list-outside pl-6 space-y-2 text-[#2c221e]">
              <li>Greenwich Mean Time (GMT) was adopted to standardise railway timetables</li>
              <li>Many new industrial machines were damaged and vandalized as a protest against the loss of jobs they caused when there were no employment options</li>
            </ul>
          </div>

          {/* Image 6: St Edmundsbury Cathedral */}
          <div className="space-y-2 my-6">
            <ArchivalImageCard
              src="/images/edmundsburycathedral.jpg"
              alt="St Edmundsbury Cathedral"
              caption="St Edmundsbury Cathedral in the Abbey Gardens, Bury St Edmunds"
              filenameHint="edmundsburycathedral.jpg"
            />
            <p className="italic text-[#5c4328] font-serif text-center sm:text-left text-sm md:text-base">
              St Edmundsbury Cathedral in the Abbey Gardens, Bury St Edmunds
            </p>
          </div>

          {/* Year 1848 */}
          <div className="bg-[#f3ede0]/70 p-5 sm:p-6 rounded-2xl border-l-4 border-[#7a5b3a] space-y-3">
            <div className="flex items-center gap-2">
              <span className="bg-[#7a5b3a] text-amber-100 font-serif font-bold text-base sm:text-lg px-3 py-1 rounded-lg">
                1848
              </span>
            </div>
            <ul className="list-disc list-outside pl-6 space-y-2 text-[#2c221e]">
              <li>Cholera was widespread throughout the UK with frequent outbreaks of scarlet fever, rabies and other diseases with high death tolls.</li>
              <li>Revolutionary movements were widespread across Europe and there was widespread political unrest combined with food shortages but British reforms averted direct uprisings.</li>
            </ul>
          </div>

          {/* Year 1849 */}
          <div className="bg-[#f3ede0]/70 p-5 sm:p-6 rounded-2xl border-l-4 border-[#7a5b3a] space-y-3">
            <div className="flex items-center gap-2">
              <span className="bg-[#7a5b3a] text-amber-100 font-serif font-bold text-base sm:text-lg px-3 py-1 rounded-lg">
                1849
              </span>
            </div>
            <ul className="list-disc list-outside pl-6 space-y-2 text-[#2c221e]">
              <li>There was an outbreak of cholera in the Suffolk area</li>
              <li>People were transported for minor offences</li>
              <li>Drunkenness was a common cause for appearances before the magistrates, with men and women in trouble</li>
              <li>Emigration continued to be seen as the answer to rural poverty and the rapidly growing population</li>
            </ul>
          </div>

        </div>

        {/* 1849 Historical Quote */}
        <blockquote className="my-8 bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] text-[#3d2a1d] font-serif text-base sm:text-lg leading-relaxed space-y-2 shadow-xs">
          <p className="italic">
            “We rejoice to find that the attention of the court of guardians of this town has been awakened to the subject of emigration. The state of the workhouse, with nearly 200 inmates, and from twenty to thirty of those able-bodied men, afford abundant proof of the necessity for taking some measures to relieve the town of its redundant population. the number of carpenters, bricklayers and others connected with the building trades, and of mere labourers, who are generally unemployed during the winter month, is this year greatly increased, and their condition aggravated by the small amount of work which they were able to procure during the summer, so that many, instead of paying off the “shop debts” of the previous winter, having added to, than reduced the score and the appeals made to the opulent or trading classes of the town have been more pressing than we almost ever remember …….There is no market for labour in the UK that is not over stocked.”<sup>5</sup>
          </p>
        </blockquote>

        {/* Footnotes Section */}
        <footer className="pt-8 mt-10 border-t-2 border-[#d8ceb9] text-sm text-[#5c4328] space-y-3 font-serif">
          <div className="flex items-center gap-2 font-bold uppercase tracking-wider text-xs text-[#7a5b3a]">
            <FileText className="w-4 h-4" />
            <span>Footnotes & Historical References</span>
          </div>
          <ol className="list-decimal list-outside pl-5 space-y-1.5 text-xs sm:text-sm leading-relaxed">
            <li>
              <a 
                href="http://www.waihiga.ac.nz/foss/denton" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[#7a5b3a] hover:underline inline-flex items-center gap-1"
              >
                <span>www.waihiga.ac.nz/foss/denton</span>
                <ExternalLink className="w-3 h-3 inline" />
              </a>
            </li>
            <li>The name Denton first appears in English records in 1169, adopted from the name Robert De Denton “man of the valley”</li>
            <li>The spelling is often with an e, and as often without an e</li>
            <li>Frances (1787 – 1851), daughter of Sir Charles Davers, MP</li>
            <li>Bury Post and Suffolk Chronicle, January 1849</li>
          </ol>
        </footer>

      </div>

      {/* Part Navigation Footer */}
      <div className="flex flex-wrap items-center justify-between gap-4 p-6 bg-[#f3ede0] rounded-3xl border-2 border-[#d8ceb9]">
        <button
          onClick={() => onSelectPart && onSelectPart('hub')}
          className="flex items-center gap-2 px-4 py-2 bg-[#e8dec9] hover:bg-[#d8ceb9] text-[#2c221e] rounded-xl font-serif font-bold text-sm transition"
        >
          <ChevronRight className="w-4 h-4 rotate-180" />
          <span>The Dentons Hub</span>
        </button>

        <button
          onClick={() => onSelectPart && onSelectPart('denton-first-gen')}
          className="flex items-center gap-2 px-5 py-2.5 bg-[#7a5b3a] hover:bg-[#5c4328] text-white rounded-xl font-serif font-bold text-sm transition shadow-xs ml-auto"
        >
          <span>Next: Part 2 — What happened to the first generation Dentons</span>
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

    </div>
  );
};
